// ZIP batch upload helper.
// Given a ZIP File, extracts MP3/audio entries + image entries, parses ID3 metadata for each,
// matches covers by filename when possible, and returns a normalized list of "tracks-to-upload".
//
// Pure client-side: zero backend involvement.

import JSZip from "jszip";
import { readAudioMetadata, coverDataUrlToFile } from "./mp3meta";

export const MAX_TRACKS = 25;
export const MAX_TOTAL_BYTES = 600 * 1024 * 1024; // 600 MB

const AUDIO_EXTS = ["mp3", "m4a", "aac", "wav", "ogg", "flac"];
const IMAGE_EXTS = ["jpg", "jpeg", "png", "webp"];
const VIDEO_EXTS = ["mp4", "webm"];

function fileExt(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toLowerCase() : "";
}

function basename(path) {
  return path.split("/").pop();
}

function stem(name) {
  return basename(name).replace(/\.[a-z0-9]+$/i, "");
}

// Skip noise: macOS resource forks, hidden files, junk
function isJunk(path) {
  if (!path) return true;
  if (path.includes("__MACOSX/")) return true;
  const base = basename(path);
  if (base.startsWith(".")) return true; // .DS_Store, .hidden
  if (base.startsWith("._")) return true;
  return false;
}

function mimeForImage(ext) {
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  return "image/jpeg";
}

// Deterministic preview URL for an embedded cover so the parent can render it
// without re-parsing.
function asObjectUrl(blob) {
  try {
    return URL.createObjectURL(blob);
  } catch (_e) {
    return null;
  }
}

/**
 * @returns {Promise<{rows: Array, errors: Array<string>, summary: {audio: number, image: number, skipped: number, totalBytes: number}}>}
 */
export async function extractZipBatch(file) {
  const errors = [];
  let zip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch (e) {
    return { rows: [], errors: [`Could not open ZIP: ${e?.message || e}`], summary: null };
  }

  // Collect candidate audio + image entries
  const audioEntries = [];
  const imageEntries = [];
  let skipped = 0;
  let totalBytes = 0;

  zip.forEach((relPath, entry) => {
    if (entry.dir) return;
    if (isJunk(relPath)) {
      skipped++;
      return;
    }
    const ext = fileExt(relPath);
    if (AUDIO_EXTS.includes(ext)) {
      audioEntries.push({ path: relPath, entry, ext });
    } else if (IMAGE_EXTS.includes(ext)) {
      imageEntries.push({ path: relPath, entry, ext });
    } else if (VIDEO_EXTS.includes(ext)) {
      // Videos aren't supported in batch upload — single-track flow handles those.
      skipped++;
      errors.push(`Skipped video file inside ZIP: ${basename(relPath)} — upload videos as single tracks`);
    } else {
      skipped++;
    }
  });

  if (audioEntries.length === 0) {
    return {
      rows: [],
      errors: errors.length ? errors : [`No audio files found in this ZIP (looking for ${AUDIO_EXTS.join(", ")}).`],
      summary: null,
    };
  }
  if (audioEntries.length > MAX_TRACKS) {
    return {
      rows: [],
      errors: [`Too many tracks (${audioEntries.length}). Max ${MAX_TRACKS} per ZIP — please split into multiple ZIPs.`],
      summary: null,
    };
  }

  // Resolve image entries to blobs upfront so we can reuse for filename matching
  const imageMap = new Map(); // stem(name) -> { blob, ext, mime, name }
  for (const img of imageEntries) {
    try {
      const blob = await img.entry.async("blob");
      const mime = mimeForImage(img.ext);
      imageMap.set(stem(img.path).toLowerCase(), {
        blob,
        ext: img.ext,
        mime,
        name: basename(img.path),
      });
      totalBytes += blob.size;
    } catch (_e) {
      // Ignore individual image failures
    }
  }

  // Process each audio entry
  const rows = [];
  for (const a of audioEntries) {
    let audioBlob;
    try {
      audioBlob = await a.entry.async("blob");
    } catch (e) {
      errors.push(`Could not read ${basename(a.path)}: ${e?.message || e}`);
      continue;
    }
    totalBytes += audioBlob.size;
    if (totalBytes > MAX_TOTAL_BYTES) {
      return {
        rows: [],
        errors: [`ZIP contents exceed ${(MAX_TOTAL_BYTES / 1024 / 1024).toFixed(0)} MB. Please split into smaller batches.`],
        summary: null,
      };
    }

    // Wrap blob as a File so jsmediatags + later FormData both behave correctly.
    const audioFile = new File([audioBlob], basename(a.path), {
      type: a.ext === "mp3" ? "audio/mpeg" : `audio/${a.ext}`,
    });

    let meta = null;
    try {
      meta = await readAudioMetadata(audioFile);
    } catch (_e) {
      meta = null;
    }

    // Cover candidates: 1) embedded ID3 cover, 2) sibling image with same stem
    let coverFile = null;
    let coverPreview = null;
    let coverSource = "none";
    if (meta && meta.coverDataUrl) {
      const f = coverDataUrlToFile(meta.coverDataUrl, stem(a.path) + "-cover");
      if (f) {
        coverFile = f;
        coverPreview = meta.coverDataUrl;
        coverSource = "embedded";
      }
    }
    if (!coverFile) {
      const siblingImg = imageMap.get(stem(a.path).toLowerCase());
      if (siblingImg) {
        coverFile = new File([siblingImg.blob], siblingImg.name, { type: siblingImg.mime });
        coverPreview = asObjectUrl(siblingImg.blob);
        coverSource = "sibling-image";
      }
    }

    // Build row defaults — fall back to filename-stem for title if missing.
    const fallbackTitle = stem(a.path).replace(/[_-]+/g, " ").trim();
    rows.push({
      id: rows.length + 1, // stable per-batch id
      filename: basename(a.path),
      audioFile,
      title: meta?.title || fallbackTitle,
      titleFromMeta: !!meta?.title,
      artist: meta?.artist || "",
      album: meta?.album || "",
      genre: meta?.genre || "",
      main_genre: "",
      lyrics: meta?.lyrics || "",
      year: meta?.year || "",
      coverFile,
      coverPreview,
      coverSource, // "embedded" | "sibling-image" | "none"
      hasEmbeddedLyrics: !!meta?.lyrics,
      include: true,
      // Admin-only fields (only used by AdminUploadForm batch wiring)
      station: "",
      mood: "",
      energy: 3,
      in_rotation: true,
      featured: false,
    });
  }

  return {
    rows,
    errors,
    summary: {
      audio: audioEntries.length,
      image: imageEntries.length,
      skipped,
      totalBytes,
    },
  };
}
